import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  ComponentType,
  ComponentProps,
} from 'react';
import { createRoot, Root } from 'react-dom/client';
import { v4 as uuid } from 'uuid';
import { ThemeProvider } from '@workboard/wobo-ui';

import { ToastNotificationProvider } from '../notifications';
import { GlobalConfigProvider } from '../global/ConfigProvider';
import { ErrorBoundary } from '../ErrorBoundary';

type ModalProps = {
  onClose: () => void;
};

export function useModal<C extends ComponentType>(
  Component: C,
  props: Omit<ComponentProps<C>, keyof ModalProps>,
  initialState = false
) {
  const [elId, setElId] = useState(initialState ? uuid() : undefined);
  const show = useCallback(() => setElId(uuid()), []);
  const hide = useCallback(() => setElId(false), []);
  const root = useRef<Root>();

  const handleClose = useCallback(() => {
    root.current?.unmount();
    hide();
  }, [hide]);

  useEffect(() => {
    if (elId) {
      let mountEl = document.getElementById(elId);
      if (!mountEl) {
        mountEl = document.createElement('div');
        mountEl.setAttribute('id', elId);
        document.body.appendChild(mountEl);
      }
      root.current = createRoot(mountEl);
      root.current.render(
        <ErrorBoundary>
          <ThemeProvider>
            <GlobalConfigProvider>
              <ToastNotificationProvider>
                <Component {...(props as any)} onClose={handleClose} />
              </ToastNotificationProvider>
            </GlobalConfigProvider>
          </ThemeProvider>
        </ErrorBoundary>,
      );

      return () => {
        root.current.unmount();
        mountEl.parentElement.removeChild(mountEl);
      };
    }
    return () => null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elId, Component, handleClose]);

  return {
    show,
    hide,
    visible: Boolean(elId),
  };
}
